import { normalizeFormElementType } from '@/common/utils/formElementType'
import { setType } from '@/common/utils/typedValueSerializer'

function isTypedCell(v: unknown): v is { type: string; value: unknown } {
  return (
    v != null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    'type' in (v as object) &&
    'value' in (v as object)
  )
}

function normalizeNestedTableTypedValue(v: any): any {
  if (!isTypedCell(v)) return v
  const t = v.type
  const val = v.value
  if (
    (t === 'UniDataTable' || t === 'UniDataTableFields') &&
    val &&
    typeof val === 'object' &&
    !Array.isArray(val) &&
    Array.isArray((val as { rows?: unknown }).rows)
  ) {
    const o = val as { rows: any[]; empty?: boolean; attributes?: unknown; columns?: unknown }
    return {
      ...v,
      value: {
        ...o,
        rows: o.rows.map((r) => normalizeMultiselectTableRow(r)),
      },
    }
  }
  return v
}

export function normalizeMultiselectTableRow(row: any): {
  fields: Record<string, any>
  errors: Record<string, any>
  attributes: Record<string, any>
} {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) {
    return { fields: {}, errors: {}, attributes: {} }
  }

  const r = row as Record<string, any>
  let fieldSource: Record<string, any> = {}

  if (r.fields != null && typeof r.fields === 'object' && !Array.isArray(r.fields)) {
    const f = r.fields as Record<string, any>
    if (f.fields != null && typeof f.fields === 'object' && !Array.isArray(f.fields)) {
      fieldSource = f.fields as Record<string, any>
    } else {
      fieldSource = f
    }
  } else {
    const skip = new Set(['errors', 'attributes', 'fields', 'type', 'value'])
    for (const [k, v] of Object.entries(r)) {
      if (skip.has(k)) continue
      fieldSource[k] = v
    }
  }

  const fields: Record<string, any> = {}
  for (const [k, v] of Object.entries(fieldSource)) {
    if (k === 'fields' || k === 'errors' || k === 'attributes') continue
    const cell = isTypedCell(v) ? v : setType(v)
    fields[k] = normalizeNestedTableTypedValue(cell)
  }

  return {
    fields,
    errors: r.errors && typeof r.errors === 'object' && !Array.isArray(r.errors) ? r.errors : {},
    attributes:
      r.attributes && typeof r.attributes === 'object' && !Array.isArray(r.attributes) ? r.attributes : {},
  }
}

export type MultiselectTableValue = {
  rows: ReturnType<typeof normalizeMultiselectTableRow>[]
  empty: boolean
  columns: string[]
}

function collectMultiselectRowFieldNames(
  row: ReturnType<typeof normalizeMultiselectTableRow>,
): string[] {
  return Object.keys(row.fields ?? {}).filter(
    (k) => k !== 'fields' && k !== 'errors' && k !== 'attributes',
  )
}

export function alignMultiselectTableSchema(
  normalizedRows: ReturnType<typeof normalizeMultiselectTableRow>[],
  existingColumns?: string[] | null,
): MultiselectTableValue {
  const preservedColumns = Array.isArray(existingColumns)
    ? existingColumns.filter((c) => typeof c === 'string' && c.trim() !== '')
    : []

  if (normalizedRows.length === 0) {
    return { rows: [], empty: true, columns: preservedColumns }
  }

  const columns =
    preservedColumns.length > 0 ? preservedColumns : collectMultiselectRowFieldNames(normalizedRows[0])

  return {
    rows: normalizedRows,
    empty: false,
    columns,
  }
}

function extractExistingColumns(v: unknown): string[] | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
  const cols = (v as { columns?: unknown }).columns
  if (!Array.isArray(cols)) return undefined
  const names = cols.filter((c): c is string => typeof c === 'string' && c.trim() !== '')
  return names.length > 0 ? names : undefined
}

export function normalizeMultiSelectFieldValue(value: any): MultiselectTableValue {
  let v = value
  if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v && 'type' in v) {
    const inner = (v as { value: unknown }).value
    if (inner && typeof inner === 'object' && !Array.isArray(inner) && Array.isArray((inner as { rows?: unknown }).rows)) {
      v = inner
    }
  }
  const existingColumns = extractExistingColumns(v)
  let rows: any[] = []
  if (v && typeof v === 'object' && Array.isArray(v.rows)) {
    rows = v.rows
  } else if (v && typeof v === 'object' && (v as any).value && Array.isArray((v as any).value.rows)) {
    rows = (v as any).value.rows
  } else if (Array.isArray(v)) {
    rows = v
  }
  const normalizedRows = rows.map((r) => normalizeMultiselectTableRow(r))
  return alignMultiselectTableSchema(normalizedRows, existingColumns)
}

/**
 * TypedValue для поля MultiSelect на middle: всегда type=UniDataTable
 */
export function toMultiSelectTypedValue(value: any): {
  type: 'UniDataTable'
  value: MultiselectTableValue
} {
  return {
    type: 'UniDataTable',
    value: normalizeMultiSelectFieldValue(value),
  }
}

export function tryPromoteStringWrappedUniDataTable(entry: unknown):
  | ReturnType<typeof toMultiSelectTypedValue>
  | undefined {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return undefined
  const e = entry as { type?: unknown; value?: unknown }
  if (e.type !== 'String') return undefined
  const inner = e.value
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return undefined
  const it = inner as { type?: string; value?: unknown }
  if (it.type !== 'UniDataTable' && it.type !== 'UniDataTableFields') return undefined
  const tbl = it.value
  if (
    !tbl ||
    typeof tbl !== 'object' ||
    Array.isArray(tbl) ||
    !Array.isArray((tbl as { rows?: unknown }).rows)
  ) {
    return undefined
  }
  return toMultiSelectTypedValue(inner)
}

/**
 * Нормализует MultiSelect-поля в clientData перед execute-script / onClick:
 * middle ожидает UniDataTable (SwingMultiSelect.getData), не String.
 */
export function applyMultiSelectToClientDataFieldsFromRegistry(
  clientDataFields: Record<string, any>,
  mergedFlatParentData: Record<string, any>,
  elementsRegistry: Record<string, any> | null | undefined,
): void {
  if (!elementsRegistry || typeof elementsRegistry !== 'object') return

  for (const reg of Object.values(elementsRegistry)) {
    if (!reg || typeof reg !== 'object') continue
    const elType = String(reg?.elementType ?? normalizeFormElementType(reg) ?? '').toUpperCase()
    if (elType !== 'MULTISELECT') continue

    const fieldName = String(reg.fieldName ?? '').trim()
    if (!fieldName) continue

    const raw =
      clientDataFields[fieldName] !== undefined
        ? clientDataFields[fieldName]
        : mergedFlatParentData[fieldName]

    const normalized =
      tryPromoteStringWrappedUniDataTable(raw) ?? toMultiSelectTypedValue(raw)

    clientDataFields[fieldName] = normalized

    for (const suffix of ['__listbox', '__items', '__action']) {
      delete clientDataFields[`${fieldName}${suffix}`]
    }
  }
}
